import { useState, useEffect } from 'react';
import './RetailHealthModel.css';
import { Modal } from 'react-bootstrap';
import { useAuth } from '../../auth';
import { RetailHealthForm, RetailHealthFormValues } from './RetailHealthForm';
import { RetailHealthList, RetailHealthData } from './RetailHealthList';
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG';

type Props = {
  show: boolean;
  handleClose: () => void;
};

const WEBHOOK_URL = 'https://etl.futurisk.in/webhook/8d19fbd1-10f5-47c9-b101-275e683ba5ff';
const LOAD_REQUESTS_URL = 'https://etl.futurisk.in/webhook/2f11dbd5-e878-466a-a371-97540cb6c6dc';

const RetailHealthModel: React.FC<Props> = ({ show, handleClose }) => {
  const auth = useAuth();
  const [requests, setRequests] = useState<RetailHealthData[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [redirectingUrl, setRedirectingUrl] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Load vehicles when modal opens
  useEffect(() => {
    let mounted = true;
    const loadVehicles = async () => {
      if (!show) {
        // Reset state when modal closes
        setRequests([]);
        setShowForm(false);
        setIsSubmitted(false);
        setSubmissionError(null);
        setRedirectingUrl(null);
        return;
      }
      
      setLoading(true);
      setShowForm(false);
      setIsSubmitted(false);
      setSubmissionError(null);
      setRedirectingUrl(null);
      setRequests([]);
      
      try {
        const payload = {
          CRMClientId: auth.currentUser?.CRMClientId,
          EmployeeId: auth.currentUser?.EmployeeId,
        };

        const resp = await fetch(LOAD_REQUESTS_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(auth.currentUser?.sessioncode ? { SessionCode: auth.currentUser.sessioncode } : {}),
          },
          body: JSON.stringify(payload),
        });

        if (!mounted) return;

        if (resp.ok) {
          const data = await resp.json();
          // Handle both single object and array responses
          let requestList: RetailHealthData[];
          if (Array.isArray(data)) {
            requestList = data;
          } else if (data && typeof data === 'object' && data.ID) {
            // Single vehicle returned as object
            requestList = [data];
          } else {
            requestList = [];
          }
          setRequests(requestList);
          
          // If no vehicles, show form directly
          if (requestList.length === 0) {
            setShowForm(true);
          } else {
            setShowForm(false);
          }
        } else {
          // On error, show form
          setRequests([]);
          setShowForm(true);
        }
      } catch (err) {
        if (mounted) {
          // On error, show form
          setRequests([]);
          setShowForm(true);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadVehicles();
    return () => { mounted = false; };
  }, [show, auth.currentUser]);

  const handleFormSubmit = async (values: RetailHealthFormValues) => {
    setSubmissionError(null);
    setCreating(true);
    try {
      const payload = {
        Name: values.Name,
        emailId: values.emailId,
        phoneNumber: values.phoneNumber,
        policyType: values.policyType,
        remarks: values.remarks,
        CRMClientId: auth.currentUser?.CRMClientId,
        EmployeeId: auth.currentUser?.EmployeeId,
      };

      const resp = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(auth.currentUser?.sessioncode ? { SessionCode: auth.currentUser.sessioncode } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const text = await resp.text();
        throw new Error(`Server error: ${resp.status} ${text}`);
      }

      const data = await resp.json();

      let redirectUrl: string | undefined;
      try {
        if (Array.isArray(data) && data.length > 0 && data[0].body?.responseData?.url) {
          redirectUrl = data[0].body.responseData.url;
        } else if (data?.body?.responseData?.url) {
          redirectUrl = data.body.responseData.url;
        } else if (data?.responseData?.url) {
          redirectUrl = data.responseData.url;
        }
      } catch (e) {
        // ignore parsing errors
      }

      setIsSubmitted(true);

      if (redirectUrl) {
        setRedirectingUrl(redirectUrl);
        try { window.open(redirectUrl, '_blank'); } catch (e) {}
        setTimeout(() => handleClose(), 800);
      } else {
        // No redirect URL - reload vehicles to show the new entry
        setTimeout(async () => {
          setIsSubmitted(false);
          setShowForm(false);
          setLoading(true);
          // Reload vehicles
          try {
            const loadPayload = {
              CRMClientId: auth.currentUser?.CRMClientId,
              EmployeeId: auth.currentUser?.EmployeeId,
            };
            const loadResp = await fetch(LOAD_REQUESTS_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(auth.currentUser?.sessioncode ? { SessionCode: auth.currentUser.sessioncode } : {}),
              },
              body: JSON.stringify(loadPayload),
            });
            if (loadResp.ok) {
              const loadData = await loadResp.json();
              let reloadedRequests: RetailHealthData[];
              if (Array.isArray(loadData)) {
                reloadedRequests = loadData;
              } else if (loadData && typeof loadData === 'object' && loadData.ID) {
                reloadedRequests = [loadData];
              } else {
                reloadedRequests = [];
              }
              setRequests(reloadedRequests);
            }
          } catch (e) {
            // ignore reload errors
          } finally {
            setLoading(false);
          }
        }, 1500);
      }
    } catch (err: any) {
      setSubmissionError(err?.message || 'Submission failed');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal
      className='modal fade'
      id='kt_modal_retail_health'
      data-backdrop='static'
      tabIndex={-1}
      role='dialog'
      show={show}
      aria-hidden='true'
      dialogClassName=' modal-lg'
      onHide={handleClose}
    >
      <div className='modal-content'>
        <div className='modal-header'>
          <h5 className='modal-title'>Retail Health Request</h5>
          <div className='btn btn-icon btn-sm btn-active-light-primary ms-2' onClick={handleClose}>
            <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-3x' />
          </div>

        </div>
        <div className='modal-body'>
          {loading ? (
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">Loading requests...</div>
            </div>
          ) : redirectingUrl ? (
            <div className="text-center">
              <div className="mb-3">Redirecting you...</div>
              <div>If nothing happens, <a href={redirectingUrl} target="_blank" rel="noreferrer">click here</a>.</div>
            </div>
          ) : isSubmitted ? (
            <div className="alert alert-success" role="alert">Details submitted successfully!</div>
          ) : (showForm || requests.length === 0) ? (
            <RetailHealthForm 
                submissionError={submissionError}
                creating={creating}
                onSubmit={handleFormSubmit}
                initialName={auth.currentUser?.Name || ''}
                initialEmail={auth.currentUser?.Email! || ''}
                initialPhone={auth.currentUser?.PhoneNumber! || ''}
              />
          ) : (
            <RetailHealthList 
                requests={requests}
                onAddRequest={() => setShowForm(true)}
              />
          )}
        </div>
        <div className='modal-footer'>
          * terms and conditions apply
        </div>
      </div>
    </Modal>
  );
};

export { RetailHealthModel };